import { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { diffWordsWithSpace } from 'diff';
import {
  CheckCircle,
  ChevronRight,
  MessageSquare,
  ArrowLeft,
  Send,
  GitCompare,
  ListChecks,
  Clock,
  UserCircle,
  Pencil,
  Trash2,
  Sparkles,
  ChevronDown,
  Film,
  ExternalLink,
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Textarea from '../../components/ui/Textarea';
import api from '../../lib/api';
import { formatDate, formatDateTime, cn, isValidHttpHttpsUrl } from '../../lib/utils';
import { youtubeEmbedUrlFromVideoPageUrl } from '../../lib/youtubeEmbed';
import { toSlug } from '../../lib/slug';
import type { GlobalRole } from '../../types';
import { AIAssistantShell, type AIAssistantField } from '../../components/ai';
import type {
  WorkspaceResponse,
  WorkspaceScript,
  ScriptEventRow,
  ScriptEventType,
  WorkspaceActor,
  ScriptSiblingRow,
  WorkspacePipeline,
  LatestAiSpellingReview,
} from './scriptWorkspaceTypes';
import StudioFinalCongrats from './StudioFinalCongrats';
import StudioVersionDiff from './StudioVersionDiff';

function actorLabel(a: WorkspaceActor | null | undefined): string {
  if (!a) return 'System';
  return a.fullName || a.email || 'User';
}

function roleLabelStudio(role: GlobalRole): string {
  switch (role) {
    case 'BRAND':
      return 'Brand';
    case 'AGENCY':
      return 'Agency';
    case 'KOL':
      return 'KOL';
    case 'ADMIN':
      return 'Admin';
    default:
      return role;
  }
}

function InlineUnifiedTextDiff({
  before,
  after,
  className,
  maxHeightClass = 'max-h-48',
}: {
  before: string;
  after: string;
  className?: string;
  maxHeightClass?: string;
}) {
  const nodes = useMemo(() => diffWordsWithSpace(before, after), [before, after]);

  return (
    <div
      className={cn(
        'overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-[#eeece8] bg-[#fdfcf9] p-2 font-mono text-xs leading-relaxed dark:border-gray-700 dark:bg-gray-950',
        maxHeightClass,
        className,
      )}
    >
      {nodes.map((part, i) => {
        if (part.added) {
          return (
            <mark
              key={i}
              className="bg-emerald-200/90 px-0.5 text-emerald-950 dark:bg-emerald-900/55 dark:text-emerald-100"
            >
              {part.value}
            </mark>
          );
        }
        if (part.removed) {
          return (
            <span
              key={i}
              className="bg-red-200/90 px-0.5 text-red-900 line-through decoration-red-700 dark:bg-red-950/50 dark:text-red-200"
            >
              {part.value}
            </span>
          );
        }
        return <span key={i}>{part.value}</span>;
      })}
    </div>
  );
}

function eventTitle(type: ScriptEventType): string {
  switch (type) {
    case 'CREATED':
      return 'Script version created';
    case 'CONTENT_UPDATED':
      return 'Script text updated';
    case 'SUBMITTED_FOR_BRAND':
      return 'Submitted for brand review';
    case 'APPROVED_BY_BRAND':
      return 'Approved by brand';
    case 'REVISION_REQUESTED':
      return 'Revision requested';
    case 'BRAND_COMMENT':
      return 'Brand note';
    case 'KOL_APPROVED_SCRIPT':
      return 'KOL approved script (pre-brand gate)';
    case 'AGENCY_REQUESTED_KOL_REVISION':
      return 'Agency requested another KOL review round';
    case 'AI_SPELLING_REVIEW':
      return 'Review by AI (spelling / grammar)';
    default:
      return type;
  }
}

const studioScriptAIFieldsBase: AIAssistantField[] = [
  {
    key: 'mustHaveUsps',
    label: 'Must-have product USPs',
    type: 'textarea',
    placeholder: 'Key product features that MUST be mentioned...',
  },
  {
    key: 'keyTalkingPoints',
    label: 'Key talking points',
    type: 'textarea',
    placeholder: 'Main points the KOL should cover...',
  },
  {
    key: 'tone',
    label: 'Tone',
    type: 'select',
    options: [
      { value: 'friendly', label: 'Friendly & conversational' },
      { value: 'professional', label: 'Professional' },
      { value: 'humorous', label: 'Humorous' },
      { value: 'educational', label: 'Educational' },
      { value: 'inspirational', label: 'Inspirational' },
    ],
  },
  {
    key: 'platformFocus',
    label: 'Platform',
    type: 'select',
    options: [
      { value: 'instagram', label: 'Instagram' },
      { value: 'tiktok', label: 'TikTok' },
      { value: 'youtube', label: 'YouTube' },
    ],
  },
  {
    key: 'language',
    label: 'Language',
    type: 'select',
    options: [
      { value: 'English', label: 'English' },
      { value: 'Malay', label: 'Bahasa Melayu' },
      { value: 'Mandarin', label: 'Mandarin' },
    ],
    defaultValue: 'English',
  },
  {
    key: 'durationSeconds',
    label: 'Video duration (seconds)',
    type: 'number',
    placeholder: '60',
    defaultValue: '60',
  },
  { key: 'brandVoice', label: 'Brand voice', type: 'text', placeholder: 'e.g., Modern and youthful' },
  { key: 'avoidPhrases', label: 'Phrases to avoid', type: 'text', placeholder: 'e.g., cheap, discount' },
  {
    key: 'ctaPreference',
    label: 'Preferred call-to-action',
    type: 'text',
    placeholder: 'e.g., Visit our website, Use code X',
  },
  {
    key: 'userNotes',
    label: 'Extra notes (from brief)',
    type: 'textarea',
    placeholder: 'Campaign context for the model…',
  },
];

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  SUBMITTED_FOR_BRAND: 'Submitted for review',
  APPROVED_BY_BRAND: 'Approved',
  REVISION_REQUESTED: 'Revision requested',
};

const STATUS_BADGE: Record<string, 'secondary' | 'default' | 'success' | 'destructive'> = {
  DRAFT: 'secondary',
  SUBMITTED_FOR_BRAND: 'default',
  APPROVED_BY_BRAND: 'success',
  REVISION_REQUESTED: 'destructive',
};

interface Props {
  data: WorkspaceResponse;
  script: WorkspaceScript;
  siblings: ScriptSiblingRow[];
  scriptsIndexPath: string;
  breadcrumbScriptsLabel: string;
  role: GlobalRole | undefined;
  kolName: string;
  currentUserId: string | undefined;
  navigate: (path: string) => void;
  goBack: () => void;
  reloadWorkspace: () => Promise<void>;
  editContent: string;
  setEditContent: (v: string) => void;
  saving: boolean;
  submitting: boolean;
  actionLoading: boolean;
  gateBusy?: boolean;
  revisionError: string;
  setRevisionError: (v: string) => void;
  revisionCommentDraft: string;
  setRevisionCommentDraft: (v: string) => void;
  brandRevisionMode: boolean;
  onEnterBrandRevisionMode: () => void;
  onCancelBrandRevisionMode: () => void;
  onConfirmBrandRevision: () => Promise<boolean>;
  canEditContent: boolean;
  canSubmitForReview: boolean;
  canBrandReview: boolean;
  canKolApproveScript?: boolean;
  canAgencyRequestKolRevision?: boolean;
  onKolApproveScript?: () => void;
  onAgencyRequestKolRevision?: () => void;
  handleSave: () => void;
  handleSubmit: () => void;
  handleApprove: () => void;
}

export default function ScriptWorkspaceStudioView({
  data,
  script,
  siblings,
  scriptsIndexPath,
  breadcrumbScriptsLabel,
  role,
  kolName,
  currentUserId,
  navigate,
  goBack,
  reloadWorkspace,
  editContent,
  setEditContent,
  saving,
  submitting,
  actionLoading,
  gateBusy = false,
  revisionError,
  setRevisionError,
  revisionCommentDraft,
  setRevisionCommentDraft,
  brandRevisionMode,
  onEnterBrandRevisionMode,
  onCancelBrandRevisionMode,
  onConfirmBrandRevision,
  canEditContent,
  canSubmitForReview,
  canBrandReview,
  canKolApproveScript = false,
  canAgencyRequestKolRevision = false,
  onKolApproveScript,
  onAgencyRequestKolRevision,
  handleSave,
  handleSubmit,
  handleApprove,
}: Props) {
  const statusVariant = STATUS_BADGE[script.status] ?? 'secondary';
  const statusLabel = STATUS_LABEL[script.status] ?? script.status;

  const scriptContentAnchorRef = useRef<HTMLDivElement>(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [showBriefOpen, setShowBriefOpen] = useState(false);
  const [scriptAIGenerating, setScriptAIGenerating] = useState(false);
  const [scriptAIError, setScriptAIError] = useState<string | null>(null);
  const [scriptAIResult, setScriptAIResult] = useState<string | null>(null);
  const [scriptAIApplyBusy, setScriptAIApplyBusy] = useState(false);
  const [subtitleDraft, setSubtitleDraft] = useState('');
  const [videoUrlDraft, setVideoUrlDraft] = useState('');
  const [videoUrlError, setVideoUrlError] = useState('');
  const [subtitleUrlError, setSubtitleUrlError] = useState('');
  const [subBusy, setSubBusy] = useState(false);
  const [savedContentHash, setSavedContentHash] = useState<string | null>(null);
  const [revisionMode, setRevisionMode] = useState<'agency' | 'brand' | null>(null);
  const [revisionDraft, setRevisionDraft] = useState('');
  const [revisionFieldError, setRevisionFieldError] = useState('');

  const [brandDraft, setBrandDraft] = useState('');
  const [brandCommentBusy, setBrandCommentBusy] = useState(false);
  const [editingBrandEventId, setEditingBrandEventId] = useState<string | null>(null);
  const [editBrandText, setEditBrandText] = useState('');

  const brandComments = useMemo(() => {
    const rows = data.events.filter((e) => e.type === 'BRAND_COMMENT');
    return [...rows].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [data.events]);

  const pipeline: WorkspacePipeline = useMemo(
    () =>
      data.pipeline ?? {
        milestones: [],
        currentPhase: 1,
        displayVersion: '1',
        strictPipeline: false,
        kolScriptApprovedAt: null,
      },
    [data.pipeline]
  );

  const aiReview: LatestAiSpellingReview = useMemo(
    () =>
      data.latestAiSpellingReview ?? {
        passed: null,
        suggestions: [],
        revisedText: null,
        model: null,
        contentHash: '',
        stale: true,
      },
    [data.latestAiSpellingReview]
  );

  const [aiDiffDismissed, setAiDiffDismissed] = useState(false);

  useEffect(() => {
    setAiDiffDismissed(false);
  }, [script.id, aiReview.contentHash]);

  const briefPrefill = useMemo(() => {
    const j = script.job;
    const parts = [
      j?.description,
      j?.objectives && `Objectives: ${j.objectives}`,
      j?.keyMessages && `Key messages: ${j.keyMessages}`,
      j?.targetAudience && `Audience: ${j.targetAudience}`,
      j?.complianceRequirements && `Compliance: ${j.complianceRequirements}`,
    ].filter(Boolean);
    return parts.join('\n\n');
  }, [script.job]);

  const studioScriptAIFields = useMemo((): AIAssistantField[] => {
    return studioScriptAIFieldsBase.map((f) =>
      f.key === 'userNotes' ? { ...f, defaultValue: briefPrefill.slice(0, 4000) } : f
    );
  }, [briefPrefill]);

  const handleScriptAIGenerate = useCallback(
    async (values: Record<string, string>) => {
      try {
        setScriptAIGenerating(true);
        setScriptAIError(null);
        setScriptAIResult(null);
        const res = await api.post('/ai/scripts/generate', {
          jobId: script.job.id,
          invitationId: script.invitation?.id,
          ...values,
          durationSeconds: values.durationSeconds ? parseInt(values.durationSeconds, 10) : 60,
        });
        setScriptAIResult(res.data.scriptText || JSON.stringify(res.data.generated, null, 2));
      } catch (e: unknown) {
        const err = e as { response?: { data?: { error?: string } } };
        setScriptAIError(err.response?.data?.error || 'AI generation failed');
      } finally {
        setScriptAIGenerating(false);
      }
    },
    [script.job.id, script.invitation?.id]
  );

  const applyAiToEditor = useCallback(() => {
    if (!scriptAIResult) return;
    setScriptAIApplyBusy(true);
    try {
      setEditContent(scriptAIResult);
      setShowBriefOpen(false);
    } finally {
      setScriptAIApplyBusy(false);
    }
  }, [scriptAIResult, setEditContent]);

  const studioSub = data.studioSubmission;

  useEffect(() => {
    setSubtitleDraft(studioSub?.subtitleUrl ?? '');
  }, [studioSub?.id, studioSub?.subtitleUrl]);

  // Hash the *saved* script content so we can predict locally whether a KOL
  // approval will auto-submit (matches the agency baseline) or hand off to the
  // agency (KOL has edited the wording).
  useEffect(() => {
    let cancelled = false;
    const text = script.content ?? '';
    const subtle = (typeof window !== 'undefined' && window.crypto?.subtle) || null;
    if (!subtle) {
      setSavedContentHash(null);
      return;
    }
    const bytes = new TextEncoder().encode(text);
    subtle
      .digest('SHA-256', bytes)
      .then((buf) => {
        if (cancelled) return;
        const hex = Array.from(new Uint8Array(buf))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('');
        setSavedContentHash(hex);
      })
      .catch(() => {
        if (!cancelled) setSavedContentHash(null);
      });
    return () => {
      cancelled = true;
    };
  }, [script.content]);

  const baselineHash = script.invitation?.kolReviewContentBaselineHash ?? null;
  // Treat "no baseline" as a match (server does the same): then the KOL is the
  // first author and approval acts as the brand submit.
  const kolApproveWillAutoSubmit = !baselineHash || (savedContentHash != null && savedContentHash === baselineHash);
  const kolApproveLabel = kolApproveWillAutoSubmit ? 'Approve & send to brand' : 'Approve (agency will review your edits)';

  const createOrFocusSubmission = async (): Promise<string | null> => {
    const invId = script.invitation?.id;
    if (!invId) return null;
    if (studioSub?.id) return studioSub.id;
    setSubBusy(true);
    try {
      const res = await api.post('/submissions', { invitationId: invId, captionText: '' });
      await reloadWorkspace();
      return (res.data as { id: string }).id;
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      alert(msg || 'Could not create submission');
      return null;
    } finally {
      setSubBusy(false);
    }
  };

  const addVideoFromDraft = async () => {
    const url = videoUrlDraft.trim();
    if (!url) return;
    if (!isValidHttpHttpsUrl(url)) {
      setVideoUrlError('Enter a valid URL starting with https:// or http:// (e.g. YouTube or Google Drive).');
      return;
    }
    setVideoUrlError('');
    const sid = await createOrFocusSubmission();
    if (!sid) return;
    setSubBusy(true);
    try {
      await api.post(`/submissions/${sid}/media`, { type: 'VIDEO', url });
      setVideoUrlDraft('');
      await reloadWorkspace();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      alert(msg || 'Failed to add video');
    } finally {
      setSubBusy(false);
    }
  };

  const saveSubtitle = async () => {
    const sub = subtitleDraft.trim();
    if (sub && !isValidHttpHttpsUrl(sub)) {
      setSubtitleUrlError('Enter a valid URL starting with https:// or http://, or clear the field.');
      return;
    }
    setSubtitleUrlError('');
    const sid = studioSub?.id ?? (await createOrFocusSubmission());
    if (!sid) return;
    setSubBusy(true);
    try {
      await api.patch(`/submissions/${sid}/studio`, { subtitleUrl: sub || null });
      await reloadWorkspace();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      alert(msg || 'Failed to save subtitle');
    } finally {
      setSubBusy(false);
    }
  };

  const runAgencyQa = async () => {
    if (!studioSub?.id) return;
    setSubBusy(true);
    try {
      await api.post(`/submissions/${studioSub.id}/agency-qa-approve`);
      await reloadWorkspace();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      alert(msg || 'QA action failed');
    } finally {
      setSubBusy(false);
    }
  };

  const runBrandFinal = async () => {
    if (!studioSub?.id) return;
    setSubBusy(true);
    try {
      await api.post(`/submissions/${studioSub.id}/brand-final-approve`);
      await reloadWorkspace();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      alert(msg || 'Final approval failed');
    } finally {
      setSubBusy(false);
    }
  };

  const submitDeliverableRevision = async () => {
    if (!studioSub?.id) return;
    const trimmed = revisionDraft.trim();
    if (!trimmed) {
      setRevisionFieldError('Please describe what should change.');
      return;
    }
    setRevisionFieldError('');
    setSubBusy(true);
    try {
      await api.post(`/submissions/${studioSub.id}/request-revision`, {
        revisionComments: trimmed,
      });
      setRevisionDraft('');
      setRevisionMode(null);
      await reloadWorkspace();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setRevisionFieldError(msg || 'Failed to request revision');
    } finally {
      setSubBusy(false);
    }
  };

  const showBrandNotesPanel = canBrandReview || brandComments.length > 0;
  const canOpenBriefAi = role === 'AGENCY' || role === 'BRAND' || role === 'ADMIN';

  const submitBrandComment = async () => {
    const t = brandDraft.trim();
    if (!t || brandCommentBusy) return;
    setBrandCommentBusy(true);
    try {
      await api.post(`/scripts/${script.id}/brand-comments`, { text: t });
      setBrandDraft('');
      await reloadWorkspace();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      alert(msg || 'Failed to post note');
    } finally {
      setBrandCommentBusy(false);
    }
  };

  const saveBrandEdit = async () => {
    if (!editingBrandEventId || brandCommentBusy) return;
    const t = editBrandText.trim();
    if (!t) return;
    setBrandCommentBusy(true);
    try {
      await api.patch(`/scripts/${script.id}/brand-comments/${editingBrandEventId}`, { text: t });
      setEditingBrandEventId(null);
      setEditBrandText('');
      await reloadWorkspace();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      alert(msg || 'Failed to update note');
    } finally {
      setBrandCommentBusy(false);
    }
  };

  const deleteBrandComment = async (eventId: string) => {
    if (brandCommentBusy) return;
    if (!confirm('Delete this brand note?')) return;
    setBrandCommentBusy(true);
    try {
      await api.delete(`/scripts/${script.id}/brand-comments/${eventId}`);
      await reloadWorkspace();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      alert(msg || 'Failed to delete note');
    } finally {
      setBrandCommentBusy(false);
    }
  };

  const isDirty = canEditContent && editContent !== (script.content || '');

  const contentTypes = Array.isArray(script.job?.contentTypes)
    ? (script.job!.contentTypes as string[])
    : [];
  const hashtags = Array.isArray(script.job?.hashtags) ? (script.job!.hashtags as string[]) : [];
  const deliverablesRows: { label: string; done?: boolean }[] = Array.isArray(script.job?.deliverablesChecklist)
    ? (script.job!.deliverablesChecklist as unknown[]).map((d: unknown) => {
        if (typeof d === 'string') return { label: d };
        if (d && typeof d === 'object' && 'label' in (d as object)) {
          const o = d as { label?: string; done?: boolean };
          return { label: o.label ?? '', done: o.done };
        }
        return { label: String(d) };
      })
    : [];

  useEffect(() => {
    const prev = document.title;
    const campaign = script.job?.title || 'Campaign';
    document.title = `Script studio · ${campaign} · v${script.version}`;
    return () => {
      document.title = prev;
    };
  }, [script.job?.title, script.version]);

  useEffect(() => {
    setEditingBrandEventId(null);
    setEditBrandText('');
    setBrandDraft('');
  }, [script.id]);

  useEffect(() => {
    if (brandRevisionMode) {
      scriptContentAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [brandRevisionMode]);

  const versions = useMemo(() => {
    const map = new Map<string, ScriptSiblingRow>();
    for (const s of siblings) {
      map.set(s.id, s);
    }
    if (!map.has(script.id)) {
      map.set(script.id, {
        id: script.id,
        version: script.version,
        status: script.status,
        createdAt: script.createdAt,
        updatedAt: script.updatedAt,
      });
    }
    return [...map.values()].sort((a, b) => b.version - a.version);
  }, [siblings, script]);

  const switchVersion = (s: ScriptSiblingRow) => {
    if (s.id === script.id) return;
    const slug = toSlug(script.job?.title || 'script', s.id);
    navigate(`${scriptsIndexPath}/${slug}`);
  };

  return (
    <div
      className={cn(
        '-mx-6 -mt-6 mb-0 min-h-[calc(100vh-4rem)] bg-[#f7f6f2] px-6 py-6 text-[#22211d]',
        'dark:bg-gray-950 dark:text-gray-100',
      )}
    >
      <nav className="mb-2 flex flex-wrap items-center gap-1 text-sm text-[#77756f] dark:text-gray-400">
        <button
          type="button"
          onClick={() => navigate(scriptsIndexPath)}
          className="hover:text-[#01696f] dark:hover:text-teal-400"
        >
          {breadcrumbScriptsLabel}
        </button>
        <ChevronRight className="h-4 w-4 shrink-0" />
        <span className="max-w-md truncate font-medium text-[#22211d] dark:text-gray-100">
          {script.job?.title || 'Workspace'}
        </span>
      </nav>

      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={goBack}
            aria-label="Go back"
            className="shrink-0 text-[#22211d] dark:text-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold text-[#22211d] sm:text-2xl dark:text-gray-100">Script studio</h1>
              <Badge variant={statusVariant} className="border-[#d4d1ca] dark:border-gray-600">
                {statusLabel} · v{script.version}
              </Badge>
              {pipeline.displayVersion && (
                <Badge variant="outline" className="border-[#01696f]/40 text-[#01696f] dark:border-teal-600 dark:text-teal-400">
                  Stage {pipeline.displayVersion}
                </Badge>
              )}
              {canOpenBriefAi && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1 border-violet-300 text-violet-800 hover:bg-violet-50 dark:border-violet-700 dark:text-violet-200 dark:hover:bg-violet-950/40"
                  onClick={() => {
                    setShowBriefOpen(true);
                    setScriptAIError(null);
                    setScriptAIResult(null);
                  }}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Open AI generator (Stage 1 brief)
                </Button>
              )}
            </div>
            <p className="text-sm font-medium text-[#22211d]/90 dark:text-gray-200">
              {script.job?.title || 'Campaign'}
              <span className="font-normal text-[#77756f] dark:text-gray-400"> · {kolName}</span>
            </p>
            <p className="max-w-3xl text-sm text-[#77756f] dark:text-gray-400">
              {data.summary}
              {pipeline.strictPipeline &&
                !pipeline.kolScriptApprovedAt &&
                (script.status === 'DRAFT' || script.status === 'REVISION_REQUESTED') && (
                  <span className="mt-1 block font-medium text-amber-900 dark:text-amber-200">
                    Strict pipeline: the KOL approves first. If they don’t change anything, approval auto-submits to the
                    brand. If they edit, the agency reviews the changes before submitting.
                  </span>
                )}
              {pipeline.agencySubmitRequiredBeforeBrand &&
                (script.status === 'DRAFT' || script.status === 'REVISION_REQUESTED') && (
                  <span className="mt-1 block font-medium text-amber-900 dark:text-amber-200">
                    KOL approved with edits — the agency will review and submit to the brand.
                  </span>
                )}
              {canBrandReview && script.status === 'SUBMITTED_FOR_BRAND' && (
                <span className="font-medium text-[#01696f] dark:text-teal-400">
                  {' '}
                  Approve or request revision below the main panel. Optional notes in <strong>Brand review</strong> stay in
                  this browser only.
                </span>
              )}
            </p>
          </div>
        </div>
      </header>

      {data.nextActions.length > 0 && (
        <div
          className={cn(
            'mb-4 rounded-lg border border-blue-200 bg-blue-50/50 p-4 shadow-sm',
            'dark:border-blue-800 dark:bg-blue-950/40',
          )}
        >
          <div className="mb-2 flex items-center gap-2 text-base font-semibold text-[#22211d] dark:text-gray-100">
            <Clock className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
            Who acts next
          </div>
          <div className="space-y-3">
            {data.nextActions.map((na, i) => {
              const mine = role === na.role;
              return (
                <div
                  key={`${na.role}-${i}`}
                  className={cn(
                    'rounded-lg border p-3 text-sm',
                    mine
                      ? 'border-blue-400 bg-white shadow-sm dark:border-blue-600 dark:bg-gray-900'
                      : 'border-[#d4d1ca] bg-white/90 dark:border-gray-700 dark:bg-gray-900/70',
                  )}
                >
                  <div className="flex flex-wrap items-center gap-2 font-medium text-[#22211d] dark:text-gray-100">
                    <UserCircle className="h-4 w-4 shrink-0 text-[#77756f] dark:text-gray-400" />
                    {roleLabelStudio(na.role)}
                    {mine && (
                      <Badge variant="default" className="text-xs">
                        Your turn
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 text-gray-700 dark:text-gray-300">{na.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,2.3fr)_minmax(260px,1fr)] lg:gap-4">
        <div className="flex min-w-0 flex-col gap-4">
          <section
            className={cn(
              'flex flex-col rounded-lg border border-[#d4d1ca] bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900',
              'min-h-[min(520px,calc(100vh-12rem))] lg:min-h-[calc(100vh-14rem)]',
            )}
          >
          {versions.length > 0 && (
            <nav
              className="version-tabs mb-3 flex flex-wrap gap-1 border-b border-[#d4d1ca] pb-2 dark:border-gray-700"
              aria-label="Script versions"
            >
              {versions.map((v) => {
                const active = v.id === script.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => switchVersion(v)}
                    className={cn(
                      'version-tab group relative inline-flex min-w-[4.5rem] flex-col items-start rounded-t-md border border-b-0 px-3 py-2 text-left text-xs transition-colors',
                      active
                        ? 'version-tab-active z-[1] border-[#d4d1ca] bg-white font-semibold text-[#01696f] shadow-[0_-2px_0_#01696f] dark:border-gray-600 dark:bg-gray-900 dark:text-teal-400'
                        : 'border-transparent bg-[#f0eeea]/80 text-[#77756f] hover:bg-[#eae8e3] dark:bg-gray-800/60 dark:text-gray-400 dark:hover:bg-gray-800',
                    )}
                  >
                    <span className="v-number text-[13px] font-semibold">v{v.version}</span>
                    <span className="v-meta mt-0.5 max-w-[8rem] truncate text-[11px] opacity-90">
                      {STATUS_LABEL[v.status] ?? v.status}
                    </span>
                    {active && (
                      <span className="mt-1 text-[10px] font-medium uppercase tracking-wide text-[#01696f]/80 dark:text-teal-400/90">
                        · current
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          )}

          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-[#22211d] dark:text-gray-100">
                Version {script.version} · {statusLabel}
              </h2>
              <span className="text-xs text-[#77756f] dark:text-gray-400">
                Updated {formatDate(script.updatedAt)}
              </span>
            </div>

            <div
              className={cn(
                'grid min-h-0 flex-1 grid-cols-1 gap-3',
                showBrandNotesPanel && 'md:grid-cols-[minmax(0,1.15fr)_minmax(0,0.95fr)] md:items-start',
              )}
            >
              <div className="flex min-h-0 flex-col gap-2">
                <div
                  ref={scriptContentAnchorRef}
                  id="script-studio-content"
                  className={cn(
                    'flex min-h-0 flex-col rounded-lg border border-[#d4d1ca] bg-[#f0eeea] p-3 dark:border-gray-600 dark:bg-gray-800/80',
                    brandRevisionMode &&
                      canBrandReview &&
                      'ring-2 ring-amber-400/90 ring-offset-2 ring-offset-[#f0eeea] dark:ring-amber-500/70 dark:ring-offset-gray-800/80',
                  )}
                >
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-[#22211d] dark:text-gray-100">Script content</h3>
                    <div className="flex flex-wrap gap-2">
                      {canEditContent && isDirty && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            type="button"
                            onClick={() => setEditContent(script.content || '')}
                            className="h-8 text-xs"
                            disabled={saving}
                          >
                            Discard changes
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleSave}
                            disabled={saving}
                            className="h-8 bg-[#01696f] text-xs hover:bg-[#015a5f]"
                          >
                            {saving ? 'Saving…' : 'Save'}
                          </Button>
                        </>
                      )}
                      {canKolApproveScript && onKolApproveScript && (
                        <Button
                          size="sm"
                          type="button"
                          onClick={onKolApproveScript}
                          disabled={gateBusy || isDirty}
                          title={
                            isDirty
                              ? 'Save changes before KOL approval'
                              : kolApproveWillAutoSubmit
                                ? 'You haven’t changed the script — approving sends it directly to the brand.'
                                : 'You edited the script — approving hands it to the agency to review before the brand sees it.'
                          }
                          className="h-8 gap-1 bg-emerald-700 text-xs hover:bg-emerald-800 disabled:opacity-50"
                        >
                          {gateBusy ? 'Saving…' : kolApproveLabel}
                        </Button>
                      )}
                      {canAgencyRequestKolRevision && onAgencyRequestKolRevision && (
                        <Button
                          size="sm"
                          type="button"
                          variant="outline"
                          onClick={onAgencyRequestKolRevision}
                          disabled={gateBusy}
                          className="h-8 text-xs"
                        >
                          Request KOL review again
                        </Button>
                      )}
                      {canSubmitForReview && (
                        <Button
                          size="sm"
                          type="button"
                          onClick={handleSubmit}
                          disabled={submitting || isDirty}
                          title={isDirty ? 'Save your edits first, then submit for review' : undefined}
                          className="h-8 gap-1 bg-[#01696f] text-xs hover:bg-[#015a5f] disabled:opacity-50"
                        >
                          <Send className="h-3.5 w-3.5" />
                          {submitting ? 'Submitting…' : 'Submit for review'}
                        </Button>
                      )}
                    </div>
                  </div>
                  {canEditContent ? (
                    <div className="flex min-h-0 flex-1 flex-col gap-2">
                      <Textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        rows={18}
                        placeholder="Script content…"
                        className="min-h-[280px] flex-1 resize-y rounded-md border border-[#d4d1ca] bg-[#fdfcf9] p-3 font-mono text-sm text-[#22211d] dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100"
                      />
                      {brandRevisionMode && canBrandReview ? (
                        <p className="text-xs text-amber-900/90 dark:text-amber-200/90">
                          Request revision: edit the script here if you want to suggest updated wording (optional). Add a
                          required summary for the KOL below — when you confirm, any script edits are saved first, then
                          the formal revision request is sent.
                        </p>
                      ) : script.status === 'SUBMITTED_FOR_BRAND' ? (
                        <p className="text-xs text-amber-900/90 dark:text-amber-200/90">
                          Submitted for brand review — you can still edit and save. The brand sees updates when they
                          refresh; changes also appear in Activity as a text diff.
                        </p>
                      ) : (
                        <p className="text-xs text-[#77756f] dark:text-gray-400">
                          Edit here and save — the next person sees updates and diffs in Activity.
                        </p>
                      )}
                    </div>
                  ) : (
                    <pre className="min-h-[280px] flex-1 overflow-auto whitespace-pre-wrap rounded-md bg-[#fdfcf9] p-3 font-mono text-sm text-[#22211d] dark:bg-gray-950 dark:text-gray-100">
                      {script.content || '(No content yet)'}
                    </pre>
                  )}

                  {brandRevisionMode && canBrandReview && (
                    <div className="mt-3 space-y-2 border-t border-amber-200/90 pt-3 dark:border-amber-900/50">
                      <label
                        htmlFor="brand-revision-summary"
                        className="block text-xs font-semibold uppercase tracking-wide text-[#77756f] dark:text-gray-400"
                      >
                        Summary for the KOL (required)
                      </label>
                      <p className="text-xs text-[#77756f] dark:text-gray-500">
                        Stored on the script and sent with the revision request — separate from Brand review notes.
                      </p>
                      <textarea
                        id="brand-revision-summary"
                        value={revisionCommentDraft}
                        onChange={(e) => {
                          setRevisionCommentDraft(e.target.value);
                          if (revisionError) setRevisionError('');
                        }}
                        rows={5}
                        placeholder="Describe what should change…"
                        disabled={actionLoading}
                        className="w-full resize-y rounded-md border border-amber-200/80 bg-white px-3 py-2 text-sm text-[#22211d] focus:border-[#01696f] focus:outline-none focus:ring-2 focus:ring-[#01696f]/20 dark:border-amber-900/40 dark:bg-gray-950 dark:text-gray-100"
                      />
                      {revisionError ? (
                        <p className="text-sm text-red-600 dark:text-red-400">{revisionError}</p>
                      ) : null}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          className="bg-[#01696f] hover:bg-[#015a5f]"
                          disabled={actionLoading}
                          onClick={() => void onConfirmBrandRevision()}
                        >
                          {actionLoading ? 'Sending…' : 'Confirm request revision'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          disabled={actionLoading}
                          onClick={() => onCancelBrandRevisionMode()}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {(data.snapshots && data.snapshots.length > 0) || script.version > 1 ? (
                  <StudioVersionDiff
                    scriptId={script.id}
                    liveVersion={script.version}
                    liveContent={canEditContent ? editContent : script.content || ''}
                    snapshots={data.snapshots ?? []}
                  />
                ) : null}

                {script.status === 'APPROVED_BY_BRAND' && studioSub?.status === 'APPROVED' && (
                  <StudioFinalCongrats
                    role={role}
                    script={script}
                    jobProgress={data.jobProgress ?? null}
                    navigate={navigate}
                  />
                )}

                {script.status === 'APPROVED_BY_BRAND' && studioSub?.status !== 'APPROVED' && (() => {
                  const canEditDeliverable = role === 'KOL' || role === 'ADMIN';
                  const canAgencyQa =
                    (role === 'AGENCY' || role === 'ADMIN') &&
                    (studioSub?.status === 'PENDING_AGENCY_QA' || studioSub?.status === 'SUBMITTED');
                  const canBrandFinal =
                    (role === 'BRAND' || role === 'ADMIN') && studioSub?.status === 'PENDING_BRAND_FINAL';
                  const canAgencyRequestSubRevision =
                    (role === 'AGENCY' || role === 'ADMIN') &&
                    (studioSub?.status === 'PENDING_AGENCY_QA' || studioSub?.status === 'SUBMITTED');
                  const canBrandRequestSubRevision =
                    (role === 'BRAND' || role === 'ADMIN') && studioSub?.status === 'PENDING_BRAND_FINAL';

                  return (
                  <div className="rounded-lg border border-[#d4d1ca] bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                    <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-[#22211d] dark:text-gray-100">
                      <Film className="h-4 w-4 shrink-0 text-[#01696f]" />
                      Deliverable (video &amp; subtitle)
                    </h3>
                    <p className="mb-3 text-xs text-[#77756f] dark:text-gray-400">
                      {canEditDeliverable
                        ? 'After the script is approved, upload or link video and an optional subtitle file URL. Agency and brand QA steps follow when the strict pipeline is enabled.'
                        : 'The KOL uploads the video and (optional) subtitle file here. Use the action buttons below when the deliverable is ready for your review step.'}
                    </p>
                    {studioSub ? (
                      <p className="mb-2 text-xs font-medium text-[#22211d] dark:text-gray-200">
                        Submission v{studioSub.version} · {studioSub.status.replace(/_/g, ' ')}
                      </p>
                    ) : (
                      <p className="mb-2 text-xs italic text-[#77756f] dark:text-gray-400">
                        No submission yet — waiting on the KOL to upload the video.
                      </p>
                    )}
                    {studioSub?.media && studioSub.media.length > 0 && (
                      <div className="mb-4 space-y-4">
                        {studioSub.media.map((m, i) => {
                          const url = m.url ?? '';
                          const embedSrc =
                            m.type === 'VIDEO' && url ? youtubeEmbedUrlFromVideoPageUrl(url) : null;
                          const label = m.title?.trim() || 'Video';
                          return (
                            <div
                              key={m.id ? m.id : `media-${i}-${m.type}-${url.slice(0, 24)}`}
                              className="overflow-hidden rounded-lg border border-[#d4d1ca] bg-[#fdfcf9] dark:border-gray-600 dark:bg-gray-950"
                            >
                              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#eae8e3] px-3 py-2 dark:border-gray-700">
                                <span className="text-xs font-semibold uppercase tracking-wide text-[#77756f] dark:text-gray-400">
                                  {m.type}
                                </span>
                                {url ? (
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 rounded-md border border-[#01696f]/40 bg-white px-2.5 py-1 text-xs font-medium text-[#01696f] hover:bg-[#01696f]/5 dark:border-teal-500/40 dark:bg-gray-900 dark:text-teal-300 dark:hover:bg-teal-950/40"
                                  >
                                    <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                                    Open link
                                  </a>
                                ) : null}
                              </div>
                              {embedSrc ? (
                                <div className="relative aspect-video w-full bg-black">
                                  <iframe
                                    title={label}
                                    src={embedSrc}
                                    className="absolute inset-0 h-full w-full"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                  />
                                </div>
                              ) : url ? (
                                <div className="px-3 py-3 text-xs text-[#22211d] dark:text-gray-200">
                                  <p className="mb-1 font-medium">{label}</p>
                                  <p className="break-all text-[#77756f] dark:text-gray-400">{url}</p>
                                </div>
                              ) : (
                                <p className="px-3 py-2 text-xs text-gray-500">No URL on this media row.</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {studioSub?.subtitleUrl && !canEditDeliverable && (
                      <div className="mb-3 rounded-lg border border-[#d4d1ca] bg-[#fdfcf9] px-3 py-2 dark:border-gray-600 dark:bg-gray-950">
                        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#77756f] dark:text-gray-400">
                          Subtitle file
                        </div>
                        <a
                          href={studioSub.subtitleUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#01696f] underline dark:text-teal-400"
                        >
                          <ExternalLink className="h-4 w-4 shrink-0" />
                          {studioSub.subtitleUrl.length > 64
                            ? `${studioSub.subtitleUrl.slice(0, 64)}…`
                            : studioSub.subtitleUrl}
                        </a>
                      </div>
                    )}

                    {canEditDeliverable && (
                      <>
                        <label className="mb-1 block text-xs font-semibold text-[#77756f] dark:text-gray-400">
                          Video URL (creates submission if needed)
                        </label>
                        <div className="mb-3 flex flex-wrap gap-2">
                          <input
                            type="url"
                            value={videoUrlDraft}
                            onChange={(e) => {
                              setVideoUrlDraft(e.target.value);
                              setVideoUrlError('');
                            }}
                            placeholder="https://…"
                            className="min-w-[200px] flex-1 rounded-md border border-[#d4d1ca] bg-[#fdfcf9] px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100"
                          />
                          <Button
                            type="button"
                            size="sm"
                            className="h-8 bg-[#01696f] text-xs hover:bg-[#015a5f]"
                            disabled={subBusy || !videoUrlDraft.trim()}
                            onClick={() => void addVideoFromDraft()}
                          >
                            Add video
                          </Button>
                        </div>
                        {videoUrlError ? (
                          <p className="mb-3 text-xs text-red-600 dark:text-red-400">{videoUrlError}</p>
                        ) : null}
                        <label className="mb-1 block text-xs font-semibold text-[#77756f] dark:text-gray-400">
                          Subtitle file URL (optional)
                        </label>
                        <div className="mb-3 flex flex-wrap gap-2">
                          <input
                            type="url"
                            value={subtitleDraft}
                            onChange={(e) => {
                              setSubtitleDraft(e.target.value);
                              setSubtitleUrlError('');
                            }}
                            placeholder="https://…"
                            className="min-w-[200px] flex-1 rounded-md border border-[#d4d1ca] bg-[#fdfcf9] px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs"
                            disabled={subBusy}
                            onClick={() => void saveSubtitle()}
                          >
                            Save subtitle
                          </Button>
                        </div>
                        {subtitleUrlError ? (
                          <p className="mb-3 text-xs text-red-600 dark:text-red-400">{subtitleUrlError}</p>
                        ) : null}
                      </>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {canAgencyQa && (
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 bg-[#01696f] text-xs hover:bg-[#015a5f]"
                          disabled={subBusy || !studioSub}
                          onClick={() => void runAgencyQa()}
                        >
                          Pass agency QA → brand final
                        </Button>
                      )}
                      {canBrandFinal && (
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 bg-emerald-700 text-xs hover:bg-emerald-800"
                          disabled={subBusy}
                          onClick={() => void runBrandFinal()}
                        >
                          Final approve deliverable
                        </Button>
                      )}
                      {canAgencyRequestSubRevision && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 border-amber-300 text-xs text-amber-900 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-950/30"
                          disabled={subBusy || !studioSub}
                          onClick={() => {
                            setRevisionMode('agency');
                            setRevisionDraft(studioSub?.revisionComments || '');
                            setRevisionFieldError('');
                          }}
                        >
                          Request KOL revision
                        </Button>
                      )}
                      {canBrandRequestSubRevision && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 border-amber-300 text-xs text-amber-900 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-950/30"
                          disabled={subBusy || !studioSub}
                          onClick={() => {
                            setRevisionMode('brand');
                            setRevisionDraft(studioSub?.revisionComments || '');
                            setRevisionFieldError('');
                          }}
                        >
                          Request revision
                        </Button>
                      )}
                    </div>

                    {revisionMode && (
                      <div className="mt-3 space-y-2 rounded-md border border-amber-300/80 bg-amber-50/70 p-3 dark:border-amber-900/60 dark:bg-amber-950/30">
                        <label
                          htmlFor="deliverable-revision-comments"
                          className="block text-xs font-semibold uppercase tracking-wide text-[#77756f] dark:text-gray-400"
                        >
                          {revisionMode === 'brand'
                            ? 'Tell the KOL what to change (required)'
                            : 'Tell the KOL what to fix before brand final (required)'}
                        </label>
                        <textarea
                          id="deliverable-revision-comments"
                          value={revisionDraft}
                          onChange={(e) => {
                            setRevisionDraft(e.target.value);
                            if (revisionFieldError) setRevisionFieldError('');
                          }}
                          rows={4}
                          disabled={subBusy}
                          placeholder="Describe what should change…"
                          className="w-full resize-y rounded-md border border-amber-200/80 bg-white px-3 py-2 text-sm text-[#22211d] focus:border-[#01696f] focus:outline-none focus:ring-2 focus:ring-[#01696f]/20 dark:border-amber-900/40 dark:bg-gray-950 dark:text-gray-100"
                        />
                        {revisionFieldError && (
                          <p className="text-sm text-red-600 dark:text-red-400">{revisionFieldError}</p>
                        )}
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            className="h-8 bg-[#01696f] text-xs hover:bg-[#015a5f]"
                            disabled={subBusy}
                            onClick={() => void submitDeliverableRevision()}
                          >
                            {subBusy ? 'Sending…' : 'Confirm request revision'}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs"
                            disabled={subBusy}
                            onClick={() => {
                              setRevisionMode(null);
                              setRevisionDraft('');
                              setRevisionFieldError('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })()}
              </div>

                {showBrandNotesPanel && (
                  <div className="flex min-h-0 flex-col gap-3 md:min-w-0">
                    <div className="flex flex-col items-center" aria-hidden>
                      <div className="h-8 w-px shrink-0 rounded-full bg-[#01696f]/50 dark:bg-teal-500/40" />
                      <span className="sr-only">Brand review applies to script version {script.version}</span>
                    </div>
                  <div
                    className={cn(
                      'shrink-0 rounded-2xl border border-[#d4d1ca] border-l-4 border-l-[#01696f] bg-gradient-to-br from-[#e8f5e9]/70 via-white to-white p-4 shadow-sm',
                      'dark:border-gray-600 dark:from-green-950/25 dark:via-gray-900 dark:to-gray-900',
                    )}
                  >
                    <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#77756f] dark:text-gray-400">
                        Brand review · v{script.version}
                      </p>
                      {!canBrandReview && brandComments.length > 0 && (
                        <p className="text-xs text-[#77756f] dark:text-gray-500">Notes from the brand on this version</p>
                      )}
                    </div>

                    {brandComments.length > 0 && (
                      <ul className="mb-4 space-y-3">
                        {brandComments.map((ev) => {
                          const meta = ev.metadata && typeof ev.metadata === 'object' ? ev.metadata : {};
                          const noteText = typeof meta.text === 'string' ? meta.text : '';
                          const editedAt = typeof meta.editedAt === 'string' ? meta.editedAt : null;
                          const authorId = ev.actor?.id;
                          const canMutate =
                            !!currentUserId && (authorId === currentUserId || role === 'ADMIN');
                          const isEditing = editingBrandEventId === ev.id;

                          return (
                            <li
                              key={ev.id}
                              className="rounded-xl border border-[#d4d1ca] bg-white/95 p-3 dark:border-gray-600 dark:bg-gray-950/80"
                            >
                              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#eeece8] pb-2 dark:border-gray-700">
                                <p className="text-xs font-medium text-[#22211d] dark:text-gray-200">
                                  {actorLabel(ev.actor)}{' '}
                                  <span className="font-normal text-[#77756f] dark:text-gray-500">
                                    · {formatDateTime(ev.createdAt)}
                                    {editedAt ? ` · edited ${formatDateTime(editedAt)}` : ''}
                                  </span>
                                </p>
                                {canMutate && !isEditing && (
                                  <div className="flex gap-1">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-7 gap-1 px-2 text-xs"
                                      disabled={brandCommentBusy}
                                      onClick={() => {
                                        setEditingBrandEventId(ev.id);
                                        setEditBrandText(noteText);
                                      }}
                                    >
                                      <Pencil className="h-3 w-3" />
                                      Edit
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-7 gap-1 border-red-200 px-2 text-xs text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/40"
                                      disabled={brandCommentBusy}
                                      onClick={() => void deleteBrandComment(ev.id)}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                      Delete
                                    </Button>
                                  </div>
                                )}
                              </div>
                              {isEditing ? (
                                <div className="mt-2 space-y-2">
                                  <textarea
                                    value={editBrandText}
                                    onChange={(e) => setEditBrandText(e.target.value)}
                                    rows={4}
                                    disabled={brandCommentBusy}
                                    className="w-full resize-y rounded-lg border border-[#d4d1ca] bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100"
                                  />
                                  <div className="flex flex-wrap gap-2">
                                    <Button
                                      type="button"
                                      size="sm"
                                      className="h-8 bg-[#01696f] text-xs hover:bg-[#015a5f]"
                                      disabled={brandCommentBusy || !editBrandText.trim()}
                                      onClick={() => void saveBrandEdit()}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-8 text-xs"
                                      disabled={brandCommentBusy}
                                      onClick={() => {
                                        setEditingBrandEventId(null);
                                        setEditBrandText('');
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <p className="mt-2 whitespace-pre-wrap text-sm text-[#22211d] dark:text-gray-200">
                                  {noteText || '—'}
                                </p>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    {canBrandReview && (
                      <div>
                        <label
                          htmlFor="studio-brand-discussion"
                          className="mb-1 block text-xs font-semibold text-[#77756f] dark:text-gray-400"
                        >
                          New note (this version only — not tied to Approve or Request revision)
                        </label>
                        <textarea
                          id="studio-brand-discussion"
                          value={brandDraft}
                          onChange={(e) => setBrandDraft(e.target.value)}
                          rows={4}
                          disabled={brandCommentBusy}
                          placeholder="Add a note for the team on this draft…"
                          className="w-full resize-y rounded-xl border border-[#d4d1ca] bg-white px-3 py-2 text-sm text-[#22211d] focus:border-[#01696f] focus:outline-none focus:ring-2 focus:ring-[#01696f]/20 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100"
                        />
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            className="h-8 gap-1 bg-[#01696f] text-xs hover:bg-[#015a5f]"
                            disabled={brandCommentBusy || !brandDraft.trim()}
                            onClick={() => void submitBrandComment()}
                          >
                            <Send className="h-3.5 w-3.5" />
                            {brandCommentBusy ? 'Posting…' : 'Submit note'}
                          </Button>
                        </div>
                        <p className="mt-2 text-xs text-[#77756f] dark:text-gray-500">
                          Use the panel below this card for Approve or Request revision.
                        </p>
                      </div>
                    )}
                  </div>
                  </div>
                )}

            </div>

            {script.status === 'REVISION_REQUESTED' && script.reviewComment && (
              <div
                className={cn(
                  'shrink-0 rounded-lg border border-red-200 bg-[#ffe5e5] p-3 dark:border-red-900 dark:bg-red-950/40',
                )}
              >
                <h3 className="mb-2 text-sm font-semibold text-[#b3261e] dark:text-red-300">
                  Brand feedback on v{script.version}
                </h3>
                <p className="whitespace-pre-wrap text-sm text-[#22211d] dark:text-gray-200">{script.reviewComment}</p>
                {script.reviewedBy && (
                  <p className="mt-2 text-xs text-[#77756f] dark:text-gray-400">— {actorLabel(script.reviewedBy)}</p>
                )}
                <p className="mt-2 text-xs text-[#77756f] dark:text-gray-400">
                  This note applies to this version only. Open another version tab to work on a different draft.
                </p>
              </div>
            )}

            {script.status === 'APPROVED_BY_BRAND' && (
              <div className="flex shrink-0 items-start gap-2 rounded-lg border-l-4 border-l-green-600 bg-green-50/90 p-4 dark:bg-green-950/30">
                <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                <div>
                  <p className="font-medium text-green-900 dark:text-green-200">Approved for production</p>
                  {script.reviewedBy && (
                    <p className="mt-1 text-sm text-green-800 dark:text-green-300">By {actorLabel(script.reviewedBy)}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

          {canBrandReview && (
            <div className="rounded-lg border border-[#d4d1ca] bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <p className="mb-3 text-sm text-[#77756f] dark:text-gray-400">
                You are reviewing <strong className="text-[#22211d] dark:text-gray-200">v{script.version}</strong>.
                {brandRevisionMode ? (
                  <>
                    {' '}
                    Finish in the <strong className="text-[#22211d] dark:text-gray-200">Script content</strong> card above
                    (summary + confirm), or cancel here.
                  </>
                ) : (
                  <>
                    {' '}
                    Approve as-is, or start <strong>Request revision</strong> to edit the script inline and send formal
                    feedback to the KOL.
                  </>
                )}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={() => void handleApprove()}
                  disabled={actionLoading || brandRevisionMode}
                  title={brandRevisionMode ? 'Cancel revision flow or complete it before approving' : undefined}
                  className="gap-2 bg-[#01696f] hover:bg-[#015a5f]"
                >
                  <CheckCircle className="h-4 w-4" />
                  Approve script
                </Button>
                {brandRevisionMode ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={actionLoading}
                    className="gap-2"
                    onClick={() => onCancelBrandRevisionMode()}
                  >
                    Cancel revision
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={actionLoading}
                    className="gap-2 border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/40"
                    onClick={() => onEnterBrandRevisionMode()}
                  >
                    <MessageSquare className="h-4 w-4" />
                    Request revision
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        <aside className="flex flex-col gap-3">
          {!canBrandReview && script.status === 'SUBMITTED_FOR_BRAND' && (role === 'AGENCY' || role === 'KOL') && (
            <div className="rounded-lg border border-[#d4d1ca] bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <h2 className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#77756f] dark:text-gray-400">
                Brand review
              </h2>
              <p className="text-sm text-[#77756f] dark:text-gray-400">
                Only brand reviewers can approve or request changes. This script is <strong>submitted for brand review</strong>
                (v{script.version}).
              </p>
            </div>
          )}

          <div className="rounded-lg border border-[#d4d1ca] bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#77756f] dark:text-gray-400">
              Campaign Info
            </h2>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wide text-[#77756f] dark:text-gray-400">Campaign</p>
                <p className="font-medium text-[#22211d] dark:text-gray-100">{script.job?.title ?? '—'}</p>
                {script.job?.brandOrg?.name && <p className="text-[#77756f] dark:text-gray-400">{script.job.brandOrg.name}</p>}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-[#77756f] dark:text-gray-400">KOL</p>
                <p className="font-medium text-[#22211d] dark:text-gray-100">{kolName}</p>
                {script.invitation?.kolProfile?.user?.email && (
                  <p className="text-[#77756f] dark:text-gray-400">{script.invitation.kolProfile.user.email}</p>
                )}
              </div>
              {script.job?.products && script.job.products.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-[#77756f] dark:text-gray-400">Products</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {script.job.products.map((p, i) => (
                      <Badge key={i} variant="outline" className="border-[#d4d1ca] text-xs dark:border-gray-600">
                        {p.title ?? `Product ${i + 1}`}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-3 max-h-[min(55vh,28rem)] space-y-3 overflow-y-auto border-t border-[#eeece8] pt-3 text-sm text-[#22211d] dark:border-gray-700 dark:text-gray-200">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#77756f] dark:text-gray-400">Brief</p>
              {script.job?.description && (
                <div>
                  <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-[#77756f] dark:text-gray-400">
                    Description
                  </p>
                  <p className="whitespace-pre-wrap">{script.job.description}</p>
                </div>
              )}
              {script.job?.objectives && (
                <div>
                  <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-[#77756f] dark:text-gray-400">
                    Objectives
                  </p>
                  <p className="whitespace-pre-wrap">{script.job.objectives}</p>
                </div>
              )}
              {script.job?.keyMessages && (
                <div>
                  <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-[#77756f] dark:text-gray-400">
                    Key messages
                  </p>
                  <p className="whitespace-pre-wrap">{script.job.keyMessages}</p>
                </div>
              )}
              {contentTypes.length > 0 && (
                <p>
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#77756f] dark:text-gray-400">
                    Content types ·{' '}
                  </span>
                  {contentTypes.join(', ')}
                </p>
              )}
              {hashtags.length > 0 && (
                <p>
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#77756f] dark:text-gray-400">
                    Hashtags ·{' '}
                  </span>
                  {hashtags.join(', ')}
                </p>
              )}
              {script.job?.complianceRequirements && (
                <div>
                  <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-[#77756f] dark:text-gray-400">
                    Compliance
                  </p>
                  <p className="whitespace-pre-wrap">{script.job.complianceRequirements}</p>
                </div>
              )}
              {deliverablesRows.length > 0 && (
                <div>
                  <p className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-[#77756f] dark:text-gray-400">
                    <ListChecks className="h-3.5 w-3.5" />
                    Deliverables
                  </p>
                  <ul className="space-y-1.5">
                    {deliverablesRows.map((row, i) => (
                      <li
                        key={`${row.label}-${i}`}
                        className="rounded-md border border-[#d4d1ca] bg-[#f7f6f2] px-2 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-900/50"
                      >
                        {row.label}
                        {typeof row.done === 'boolean' && (
                          <span className="ml-2 text-[#77756f]">{row.done ? 'Done' : 'Pending'}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!script.job?.description &&
                !script.job?.objectives &&
                !script.job?.keyMessages &&
                contentTypes.length === 0 &&
                hashtags.length === 0 &&
                !script.job?.complianceRequirements &&
                deliverablesRows.length === 0 && (
                  <p className="text-[#77756f] dark:text-gray-400">No brief fields on this job yet.</p>
                )}
            </div>
          </div>

          <div className="rounded-lg border border-[#d4d1ca] bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#77756f] dark:text-gray-400">
              Stages (1–7)
            </h2>
            {pipeline.milestones.length === 0 ? (
              <p className="text-sm text-[#77756f] dark:text-gray-400">Pipeline data not available for this workspace.</p>
            ) : (
              <ol className="space-y-2">
                {pipeline.milestones.map((m) => (
                  <li
                    key={m.id}
                    className={cn(
                      'rounded-md border px-2 py-1.5 text-xs transition-colors',
                      m.state === 'done' &&
                        'border-green-200 bg-green-50/90 text-[#22211d] dark:border-green-900 dark:bg-green-950/30 dark:text-gray-100',
                      m.state === 'active' &&
                        'border-[#01696f] bg-[#e6f4f5] font-medium text-[#22211d] dark:border-teal-600 dark:bg-teal-950/40 dark:text-gray-100',
                      m.state === 'upcoming' &&
                        'border-[#e8e6e1] bg-[#f7f6f2] text-[#a8a6a1] dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500',
                    )}
                  >
                    <span className="font-semibold uppercase tracking-wide">Stage {m.phase}</span> · {m.title}{' '}
                    <span className="opacity-75">({m.displayVersion})</span>
                    <p className="mt-0.5 font-normal leading-snug opacity-95">{m.description}</p>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="rounded-lg border border-[#d4d1ca] bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#77756f] dark:text-gray-400">
              Review by AI
            </h2>
            {aiReview.stale ? (
              <p className="text-sm text-[#77756f] dark:text-gray-400">
                Save the script to run a spelling check in the background, then refresh this panel.
              </p>
            ) : aiReview.passed === true ? (
              <p className="text-sm text-green-800 dark:text-green-200">Passed automated spelling / grammar check.</p>
            ) : aiDiffDismissed ? (
              <p className="text-sm text-[#77756f] dark:text-gray-400">You dismissed this review. Save again to re-run the check.</p>
            ) : aiReview.revisedText ? (
              <div className="space-y-2">
                <p className="text-xs text-[#77756f] dark:text-gray-400">
                  Proposed edits vs your current script (red strikethrough = remove, green = add). Apply replaces the editor
                  text; reject keeps your wording.
                </p>
                <div className="mb-2 flex gap-4 text-[10px] font-semibold uppercase tracking-wide text-[#77756f]">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-5 rounded bg-emerald-200 dark:bg-emerald-900" />
                    Added
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-5 rounded bg-red-200 dark:bg-red-900" />
                    Removed
                  </span>
                </div>
                <InlineUnifiedTextDiff
                  before={canEditContent ? editContent : script.content || ''}
                  after={aiReview.revisedText}
                  maxHeightClass="max-h-64"
                />
                {canEditContent ? (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 bg-[#01696f] text-xs hover:bg-[#015a5f]"
                      onClick={() => {
                        setEditContent(aiReview.revisedText!);
                        setAiDiffDismissed(true);
                      }}
                    >
                      Apply to editor
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => setAiDiffDismissed(true)}>
                      Reject
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-[#77756f] dark:text-gray-500">Read-only: you cannot apply edits from this role.</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-[#77756f] dark:text-gray-400">
                Issues were reported for this version, but no replacement text is stored. Save the script to run a fresh
                spelling check.
              </p>
            )}
          </div>

          <div className="rounded-lg border border-[#d4d1ca] bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <button
              type="button"
              onClick={() => setActivityOpen((o) => !o)}
              className="mb-2 flex w-full items-center justify-between gap-2 text-left"
            >
              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#77756f] dark:text-gray-400">
                <GitCompare className="h-4 w-4" />
                Detailed event log
              </span>
              <ChevronDown className={cn('h-4 w-4 shrink-0 text-[#77756f] transition-transform', activityOpen && 'rotate-180')} />
            </button>
            {activityOpen &&
              (data.events.length === 0 ? (
                <p className="text-sm text-[#77756f] dark:text-gray-400">No activity recorded yet.</p>
              ) : (
                <ul className="space-y-3 border-l border-[#d4d1ca] pl-3 dark:border-gray-600">
                  {[...data.events].reverse().map((ev: ScriptEventRow) => (
                    <li key={ev.id} className="relative">
                      <span
                        className="absolute -left-[17px] top-1.5 h-2 w-2 rounded-full border-2 border-white bg-[#01696f] dark:border-gray-900"
                        aria-hidden
                      />
                      <p className="text-sm font-medium text-[#22211d] dark:text-gray-100">{eventTitle(ev.type)}</p>
                      <p className="text-xs text-[#77756f] dark:text-gray-400">
                        {actorLabel(ev.actor)} · {formatDateTime(ev.createdAt)}
                      </p>
                      {ev.type === 'REVISION_REQUESTED' &&
                        ev.metadata &&
                        typeof ev.metadata.reviewComment === 'string' && (
                          <p className="mt-2 whitespace-pre-wrap rounded border border-amber-200 bg-amber-50/90 p-2 text-sm dark:border-amber-900 dark:bg-amber-950/40">
                            {ev.metadata.reviewComment}
                          </p>
                        )}
                      {ev.type === 'BRAND_COMMENT' && ev.metadata && typeof ev.metadata.text === 'string' && (
                        <p className="mt-2 whitespace-pre-wrap rounded border border-teal-100 bg-teal-50/80 p-2 text-sm dark:border-teal-900 dark:bg-teal-950/40">
                          {ev.metadata.text}
                        </p>
                      )}
                      {ev.type === 'AI_SPELLING_REVIEW' && ev.metadata && (
                        <div className="mt-2 space-y-2 rounded border border-violet-100 bg-violet-50/80 p-2 text-xs dark:border-violet-900 dark:bg-violet-950/40">
                          {ev.metadata.passed === true ? (
                            <p>Passed automated spelling / grammar check.</p>
                          ) : typeof ev.metadata.sourceText === 'string' && typeof ev.metadata.revisedText === 'string' ? (
                            <>
                              <p className="text-[#77756f] dark:text-gray-400">Proposed edits (vs text at review time):</p>
                              <InlineUnifiedTextDiff
                                before={ev.metadata.sourceText as string}
                                after={ev.metadata.revisedText as string}
                                maxHeightClass="max-h-48"
                              />
                            </>
                          ) : (
                            <p className="text-[#77756f] dark:text-gray-400">
                              Issues found — this log entry predates inline diffs. Save the script to run a new review.
                            </p>
                          )}
                        </div>
                      )}
                      {(ev.type === 'KOL_APPROVED_SCRIPT' || ev.type === 'AGENCY_REQUESTED_KOL_REVISION') &&
                        ev.metadata &&
                        typeof ev.metadata.comment === 'string' && (
                          <p className="mt-2 whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-2 text-sm dark:border-slate-700 dark:bg-slate-900/60">
                            {ev.metadata.comment}
                          </p>
                        )}
                      {ev.type === 'CONTENT_UPDATED' && ev.metadata && (() => {
                        const m = ev.metadata as Record<string, unknown>;
                        const prev =
                          typeof m.previousContent === 'string'
                            ? m.previousContent
                            : typeof m.previousSnippet === 'string'
                              ? m.previousSnippet
                              : '';
                        const next =
                          typeof m.nextContent === 'string'
                            ? m.nextContent
                            : typeof m.nextSnippet === 'string'
                              ? m.nextSnippet
                              : '';
                        // Compact word delta. Full side-by-side diff lives in
                        // the version tabs at the top of the main panel now.
                        const nodes = prev || next ? diffWordsWithSpace(prev, next) : [];
                        let added = 0;
                        let removed = 0;
                        for (const n of nodes) {
                          if (!n.value) continue;
                          const words = n.value.match(/\S+/g)?.length ?? 0;
                          if (n.added) added += words;
                          else if (n.removed) removed += words;
                        }
                        const prevVer = typeof m.previousVersion === 'number' ? m.previousVersion : null;
                        const nextVer = typeof m.nextVersion === 'number' ? m.nextVersion : null;
                        return (
                          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#77756f] dark:text-gray-400">
                            {prevVer != null && nextVer != null && (
                              <span className="font-medium text-[#22211d] dark:text-gray-200">
                                v{prevVer} → v{nextVer}
                              </span>
                            )}
                            {(added > 0 || removed > 0) && (
                              <span>
                                <span className="text-emerald-700 dark:text-emerald-400">+{added}</span>
                                <span className="mx-0.5 opacity-60">/</span>
                                <span className="text-red-700 dark:text-red-400">-{removed}</span>
                                <span className="ml-1">words</span>
                              </span>
                            )}
                            {typeof m.previousLength === 'number' && typeof m.nextLength === 'number' && (
                              <span>
                                {String(m.previousLength)} → {String(m.nextLength)} chars
                              </span>
                            )}
                            <span className="opacity-80">View in Versions tab.</span>
                          </div>
                        );
                      })()}
                    </li>
                  ))}
                </ul>
              ))}
          </div>
        </aside>
      </div>

      <AIAssistantShell
        open={showBriefOpen}
        onClose={() => setShowBriefOpen(false)}
        title="AI script generator"
        description="Prefilled from this campaign brief (Stage 1). Generate a draft, then insert it into the script editor."
        fields={studioScriptAIFields}
        onGenerate={handleScriptAIGenerate}
        generating={scriptAIGenerating}
        error={scriptAIError}
        result={
          scriptAIResult ? (
            <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm dark:bg-gray-900 dark:text-gray-100">
              {scriptAIResult}
            </pre>
          ) : undefined
        }
        applyLabel="Insert into script editor"
        onApply={() => applyAiToEditor()}
        applying={scriptAIApplyBusy}
      />
    </div>
  );
}
